import { useState } from "react";
import { cn } from "@/lib/utils";
import { Bot, ArrowRight, FileText } from "lucide-react";

const codeExamples = {
  typescript: `import { WeatherAPI } from 'weatherapi.dev'

const weather = new WeatherAPI('your_api_key')

const current = await weather.now({
  lat: 37.7749,
  lon: -122.4194
})

// Fully typed — autocomplete just works
console.log(current.temperature.celsius) // 18.5
console.log(current.condition.text) // "Sunny"`,

  python: `from weatherapi_dev import WeatherAPI

weather = WeatherAPI('your_api_key')

current = weather.now(
    lat=37.7749,
    lon=-122.4194
)

# Type hints work in your IDE
print(current.temperature.celsius)  # 18.5
print(current.condition.text)  # "Sunny"`,

  curl: `curl -X GET "https://api.weatherapi.dev/now" \\
  -H "Authorization: Bearer your_api_key" \\
  -d "lat=37.7749" \\
  -d "lon=-122.4194"

# Response:
{
  "temperature": { "celsius": 18.5, "fahrenheit": 65.3 },
  "condition": { "code": 1000, "text": "Sunny" },
  "humidity": 68,
  "wind": { "kph": 12.8, "direction": "W" }
}`,
};

const responseExample = `// Response in ~45ms
{
  "temperature": { "celsius": 18.5, "fahrenheit": 65.3 },
  "condition": { "code": 1000, "text": "Sunny" },
  "humidity": 68,
  "wind": { "kph": 12.8, "direction": "W" }
}`;

interface HeroProps {
  className?: string;
}

export function Hero({ className }: HeroProps) {
  const [activeTab, setActiveTab] = useState<"typescript" | "python" | "curl">(
    "typescript"
  );

  const tabs = [
    { id: "typescript", label: "TypeScript" },
    { id: "python", label: "Python" },
    { id: "curl", label: "cURL" },
  ] as const;

  const stats = [
    { value: "50ms", label: "Avg response time" },
    { value: "99.99%", label: "Uptime SLA" },
    { value: "10K", label: "Free requests/mo" },
  ];

  return (
    <section
      className={cn(
        "relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden",
        className
      )}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-white -z-10" />

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-sky-200 rounded-full opacity-30 blur-3xl -z-10" />
      <div className="absolute top-40 right-10 w-96 h-96 bg-sky-100 rounded-full opacity-40 blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <a
            href="#x402"
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 hover:bg-sky-200 rounded-full text-sm font-medium text-sky-700 transition-colors"
          >
            <Bot className="w-4 h-4" />
            <span>Now with x402 — AI agents can pay per request</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 tracking-tight">
              The weather API
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-sky-600">
                developers deserve.
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Type-safe SDKs for TypeScript and Python. Edge caching that
              actually works. Pricing that makes sense. Built by developers, for
              developers.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10">
              <a
                href="/signup"
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white font-semibold rounded-xl shadow-lg shadow-sky-200 hover:shadow-xl hover:shadow-sky-300 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
              >
                Start for free <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="/docs"
                className="w-full sm:w-auto px-6 py-3 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl shadow-sm hover:shadow-md hover:border-slate-300 transition-all flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" />
                View Documentation
              </a>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-center lg:justify-start gap-8 sm:gap-12">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-slate-800">
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Code window */}
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-sky-400 to-sky-600 rounded-2xl opacity-20 blur-xl" />

            <div className="relative bg-slate-900 rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <span className="text-sm text-slate-400 font-mono">
                    weather.ts
                  </span>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        "px-3 py-1 text-xs font-medium rounded transition-colors",
                        activeTab === tab.id
                          ? "bg-slate-700 text-white"
                          : "text-slate-400 hover:text-white"
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Code content */}
              <div className="p-4 overflow-x-auto">
                <pre className="text-sm leading-relaxed">
                  <code className="text-slate-300 whitespace-pre">
                    {codeExamples[activeTab]}
                  </code>
                </pre>

                {/* Response preview */}
                {activeTab !== "curl" && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <pre className="text-sm leading-relaxed">
                      <code className="text-slate-400 whitespace-pre">
                        {responseExample}
                      </code>
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
